<script setup>
import { computed } from 'vue'
import { Head } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import PublicLayout from '@/Layouts/PublicLayout.vue'
import BrandHero from '@/Components/Public/BrandHero.vue'
import ProductCard from '@/Components/Public/ProductCard.vue'
import EmptyResults from '@/Components/Public/EmptyResults.vue'

defineOptions({ layout: PublicLayout })

const { t } = useI18n()

const props = defineProps({
  groups:     { type: Object, required: true },
  base_paths: { type: Object, required: true },
})

// One section per vertical, in aggregation order — a section is skipped
// entirely when its group is empty rather than rendering an empty heading.
const SECTIONS = [
  { key: 'charters',   kind: 'charter' },
  { key: 'tours',      kind: 'tour' },
  { key: 'transfers',  kind: 'transfer' },
  { key: 'vehicles',   kind: 'vehicle' },
  { key: 'properties', kind: 'property' },
]

const visibleSections = computed(() =>
  SECTIONS.filter((s) => (props.groups[s.key] || []).length > 0)
)
const isEmpty = computed(() => visibleSections.value.length === 0)
</script>

<template>
  <Head>
    <title>{{ t('public.vip.head_title') }}</title>
    <meta name="description" :content="t('public.vip.head_description')">
  </Head>

  <BrandHero kind="vip" :eyebrow="t('public.vip.eyebrow')" :title="t('public.vip.hero_title')">
    <p class="vip-lede">{{ t('public.vip.hero_intro') }}</p>
  </BrandHero>

  <section class="listing">
    <div class="listing__inner">
      <EmptyResults
        v-if="isEmpty"
        :title="t('public.vip.empty')"
        :clear-href="base_paths.tours"
        :clear-label="t('public.vip.groups.tours')"
      />

      <section v-for="s in visibleSections" :key="s.key" class="vip-group">
        <h2 class="vip-group__title">{{ t(`public.vip.groups.${s.key}`) }}</h2>
        <ul class="grid">
          <li v-for="item in groups[s.key]" :key="item.id">
            <ProductCard :kind="s.kind" :item="item" :href="`${base_paths[s.key]}/${item.id}`" />
          </li>
        </ul>
      </section>
    </div>
  </section>
</template>

<style scoped>
.vip-lede { margin: 1rem 0 0; font-size: clamp(1.05rem, 2vw, 1.3rem); line-height: 1.5; max-width: 52ch; opacity: 0.92; }

/* Gold accent for card hovers/type chips on this page, echoing the VIP
   badge colour so the whole page reads as one consistent luxury tier. */
.listing { --card-accent: var(--warn); background: var(--bg); }
.listing__inner { max-width: 96rem; margin: 0 auto; padding: clamp(1.75rem, 4vw, 2.75rem) clamp(1.25rem, 4vw, 3rem) 4rem; }

.vip-group { margin-bottom: clamp(2rem, 5vw, 3rem); }
.vip-group__title {
  font-family: var(--display); font-weight: 800; letter-spacing: -0.01em;
  font-size: clamp(1.15rem, 2.2vw, 1.5rem); color: var(--ink);
  margin: 0 0 1.1rem;
  padding-bottom: 0.6rem;
  border-bottom: 1px solid color-mix(in oklch, var(--warn) 35%, var(--line));
}

.grid { list-style: none; padding: 0; margin: 0; display: grid; grid-template-columns: repeat(auto-fill, minmax(16rem, 1fr)); gap: 1.25rem; }
</style>
